import { yupResolver } from '@hookform/resolvers/yup';
import {
  useEditPickRatesMutation,
  useGetPickRatesQuery,
} from 'common/api/endpoints/harvestData';
import { handleError } from 'common/api/utils/handleError';
import { DataTable, DataTableProps } from 'common/components/DataTable';
import * as notifier from 'common/services/notification';
import {
  PickRateTableRows,
  usePickRatesTableData,
} from 'features/settings-view/hooks/usePickRatesTableData';
import { FC, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { ratesSchema } from 'utils/schemas/settings';

export type PickRateValues = Record<string, number | null>;

export const PickRates: FC = () => {
  const { data: rates, isLoading, isFetching, error } = useGetPickRatesQuery();
  const [editPickRates, { isLoading: isSubmitting }] =
    useEditPickRatesMutation();
  const { columns, data, isEditing, setIsEditing } = usePickRatesTableData(
    rates || [],
    isSubmitting,
  );

  const defaultValues = useMemo(
    () =>
      (rates || []).reduce((acc, curr) => {
        acc[curr.id] = curr.rate;

        return acc;
      }, {} as PickRateValues),
    [rates],
  );

  const methods = useForm<PickRateValues>({
    values: defaultValues,
    resolver: yupResolver(ratesSchema),
    mode: 'all',
  });
  const {
    getFieldState,
    reset,
    formState: { isDirty },
  } = methods;

  const closeForm = () => {
    reset();
    setIsEditing(false);
  };

  const onSubmit = async (data: PickRateValues): Promise<void> => {
    if (!isDirty) {
      // No values to save.
      closeForm();
      return;
    }

    const dto = Object.entries(data).reduce(
      (acc, [id, rate]) => {
        if (getFieldState(id).isDirty) {
          acc.push({
            id: parseInt(id, 10),
            rate: rate || 0,
          });
        }
        return acc;
      },
      [] as { id: number; rate: number }[],
    );

    try {
      await editPickRates(dto).unwrap();
      notifier.showSuccessMessage('Pick rates updated successfully.');
      closeForm();
    } catch (e) {
      handleError(e, 'Unable to edit pick rates.');
    }
  };

  const tableProps: DataTableProps<PickRateTableRows, PickRateValues> = {
    columns,
    data,
    isLoading,
    isFetching,
    maxTableWidth: '700px',
    formProps: {
      methods,
      onSubmit,
      onCancel: closeForm,
      isEditing,
      setIsEditing,
      isDirty,
      isSubmitting,
    },
  };

  useEffect(() => {
    if (error) {
      handleError(error, 'Unable to fetch pick rates.');
    }
  }, [error]);

  return <DataTable {...tableProps} />;
};
